// Everything the Import files screen shows, derived from the files and the user's choices.
// Pure, so the Vue dialog only renders it and every rule is covered by Jest
// (importScreenState.test.ts). Ported from Android's `ImportChoices.kt` and
// `ImportScreenState.kt` (modules/importList/state), which follow the design prototype
// and docs/unified-import-spec.md → "Current design"; the names match so a question
// about one platform reads the same on the other.

import { snapStatus, type FileSnap, type SnapStatus } from './importSnap';
import { defaultFor } from './importRules';
import type { ImportAction, ImportError, ImportIntent, ImportPreview, SkipReason } from './importTypes';
import {
  bothPlannedLines,
  keptWaypoints,
  plannedDays,
  plannedLines,
  recordingCount,
  recordingDistanceM,
  recordingDurationMs,
  waypointsToAdd
} from './importPayloads';
import type { ImportResult as PeakCsvCheck } from '../../apps/peak_bagging/peakBagCsv';

export type { PeakCsvCheck };

/** What the user picked for one file in "Import as": the contract's ImportAction. */
export type ImportChoice = 'planned' | 'recording' | 'both' | 'waypoints' | 'fromWaypoints' | 'peaks' | 'skip';

/** Where an imported file ends up; also the start screen an import was opened from. */
export type ImportDestination = 'recordings' | 'plannedHikes' | 'waypoints' | 'peaks';

/** Display order everywhere: recordings → planned hikes → waypoints → peaks. */
export const IMPORT_DESTINATIONS: ImportDestination[] = ['recordings', 'plannedHikes', 'waypoints', 'peaks'];

/** Everywhere a choice saves to, in display order: "Recordings and a planned hike" saves to two. */
export function choiceDestinations(choice: ImportChoice): ImportDestination[] {
  switch (choice) {
    case 'planned':
    case 'fromWaypoints':
      return ['plannedHikes'];
    case 'recording':
      return ['recordings'];
    case 'both':
      return ['recordings', 'plannedHikes'];
    case 'waypoints':
      return ['waypoints'];
    case 'peaks':
      return ['peaks'];
    default:
      return [];
  }
}

/** Where a choice saves to first (its place in the list); null for Don't import. */
export const choiceDestination = (choice: ImportChoice): ImportDestination | null => choiceDestinations(choice)[0] ?? null;

/** The start screen's own destination; null when the screen was opened with none. */
export function intentDestination(intent: ImportIntent): ImportDestination | null {
  switch (intent) {
    case 'planned':
      return 'plannedHikes';
    case 'recording':
      return 'recordings';
    case 'waypoints':
      return 'waypoints';
    default:
      return null;
  }
}

export function actionToChoice(action: ImportAction): ImportChoice {
  return action === 'fromwpts' ? 'fromWaypoints' : action;
}

/** The per-file settings under "Import as", "Days", "Lines" and "Waypoints". */
export interface ImportFileSettings {
  choice: ImportChoice;
  /** "One day" instead of one day per line. */
  joinDays: boolean;
  /** Also import a route that copies a track in the same file (contract rule 2). */
  includeCourse: boolean;
  /** The file's named waypoints, as highlights or a waypoint group. */
  keepWaypoints: boolean;
  /** The unnamed points that just repeat the line (contract rule 3). */
  includeRepeats: boolean;
  /** Match a planned hike's days onto the path network before saving (PRO; see importSnap.ts). */
  snapToPaths: boolean;
}

export const settingsFor = (choice: ImportChoice, overrides: Partial<ImportFileSettings> = {}): ImportFileSettings => ({
  choice,
  joinDays: false,
  includeCourse: false,
  keepWaypoints: true,
  includeRepeats: false,
  snapToPaths: false,
  ...overrides
});

/**
 * One file on the screen. `preview` is null until the file has been read. A route file
 * unpacked from a ZIP archive is a file of its own, whose `source` is the ZIP that was added.
 */
export interface ImportFileEntry {
  id: string;
  name: string;
  preview: ImportPreview | null;
  /** What the start screen made of it (contract rule 6); fixed once the file is read. */
  defaultChoice: ImportChoice;
  /** Why the file doesn't fit the start screen (contract `skipReasons`), when it doesn't. */
  skipReason: SkipReason | null;
  settings: ImportFileSettings;
  /** The id of the file the user added: `id` itself, or the ZIP archive this came out of. */
  source: string;
  /**
   * A bagged-peaks CSV's dry run on the server (`importBaggedPeaksCsv`, commit: false): which
   * rows match a peak. Null until it's back, and for every other file.
   */
  peakCheck: PeakCsvCheck | null;
}

/** A bagged-peaks CSV that read fine; it goes to the server to be matched. */
export const isPeaksFile = (e: ImportFileEntry): boolean => !!e.preview && !e.preview.error && e.preview.format === 'csv';

/** Still being read — or, for a bagged-peaks CSV, still being matched. */
export const isLoading = (e: ImportFileEntry): boolean => e.preview === null || (isPeaksFile(e) && e.peakCheck === null);
export const entryError = (e: ImportFileEntry): ImportError | null => e.preview?.error ?? null;
/** Why the server couldn't match a bagged-peaks CSV (Peaks::BagCsvParser's codes, or `generic`). */
export const peakCheckError = (e: ImportFileEntry): string | null => e.peakCheck?.error ?? null;
export const fromArchive = (e: ImportFileEntry): boolean => e.source !== e.id;

/** The file's recommended choice (contract rule 5); null while reading or unreadable. */
export function recommendedChoice(e: ImportFileEntry): ImportChoice | null {
  if (!e.preview || e.preview.error || !e.preview.recommended) return null;
  return actionToChoice(e.preview.recommended);
}

/**
 * What "Import as" starts on: the contract's default, which is the file's own type. Don't
 * import only when the file has nothing it can become.
 */
export function initialChoice(e: ImportFileEntry): ImportChoice {
  return e.defaultChoice === 'skip' ? recommendedChoice(e) ?? 'skip' : e.defaultChoice;
}

/** A file that's still being read. */
export const pendingEntry = (id: string, name: string, source: string = id): ImportFileEntry => ({
  id,
  name,
  preview: null,
  defaultChoice: 'skip',
  skipReason: null,
  settings: settingsFor('skip'),
  source,
  peakCheck: null
});

/** A read file's entry: its default choice comes from the start screen (`defaultFor`). */
export function importFileEntry(id: string, name: string, preview: ImportPreview, intent: ImportIntent, source: string = id): ImportFileEntry {
  const { action, reason } = defaultFor(preview, intent);
  const entry: ImportFileEntry = {
    id,
    name,
    preview,
    defaultChoice: actionToChoice(action),
    skipReason: reason,
    settings: settingsFor('skip'),
    source,
    peakCheck: null
  };
  return { ...entry, settings: settingsFor(initialChoice(entry)) };
}

/** Where a file sits on a several-file screen. Decided by its default, so it never moves. */
export type ImportFileGroup = 'ready' | 'look' | 'error';

/** The badge on an "Import as" option: only ever the recommended one. */
export type ImportOptionBadge = 'recommended';

/** How many of each thing the import will save. */
export interface ImportCounts {
  recordings: number;
  plannedHikes: number;
  waypointGroups: number;
  /** Peaks bagged from CSVs: the rows that match a peak not yet bagged. */
  peaks: number;
}

export const countsTotal = (c: ImportCounts): number => c.recordings + c.plannedHikes + c.waypointGroups + c.peaks;

export function countOf(c: ImportCounts, d: ImportDestination): number {
  if (d === 'recordings') return c.recordings;
  if (d === 'plannedHikes') return c.plannedHikes;
  if (d === 'peaks') return c.peaks;
  return c.waypointGroups;
}

/** What the action button says. */
export type ImportCta =
  | { kind: 'nothing' }
  | { kind: 'import'; counts: ImportCounts };

/** The one stats line under a file's name. */
export type ImportFileMeta =
  | { kind: 'reading' }
  | { kind: 'unreadable'; error: ImportError }
  /** A bagged-peaks CSV the server couldn't match; `error` is its code. */
  | { kind: 'peaksError'; error: string }
  | { kind: 'skipped'; reason: SkipReason | null; intent: ImportIntent; needsLook: boolean }
  /** `count` recordings, one per recorded track; distance and time are all of them added up. */
  | { kind: 'recording'; count: number; distanceM: number; durationMs: number }
  /** `recordings` recordings plus one planned hike of `days` days. */
  | { kind: 'both'; recordings: number; days: number; highlights: number }
  | { kind: 'planned'; days: number; distanceM: number; highlights: number }
  | { kind: 'waypoints'; count: number }
  | { kind: 'fromWaypoints'; count: number; keptAsHighlights: boolean }
  /** What the server matched in a bagged-peaks CSV. */
  | { kind: 'peaks'; check: PeakCsvCheck };

/** One summary tile: how many of `destination` the import will create right now. */
export interface ImportTile {
  destination: ImportDestination;
  count: number;
}

/** The sticky "N need a look · N can't be read" bar and what it folds away. */
export interface ImportLookBar {
  lookCount: number;
  errorCount: number;
  /** Files behind the bar the user has chosen to import anyway. */
  addedCount: number;
  open: boolean;
}

/** Which settings an open file shows, and the numbers they need. */
export interface ImportFileSections {
  showPickHint: boolean;
  options: ImportChoice[];
  showDays: boolean;
  dayCount: number;
  showLines: boolean;
  /** The "Snap to paths" row, under Lines: a planned hike from the file's own lines. */
  showSnap: boolean;
  snapStatus: SnapStatus;
  showWaypoints: boolean;
  namedWaypoints: number;
  repeatedPoints: number;
  waypointsAsHighlights: boolean;
  /** A bagged-peaks CSV going to My Peaks: what matched, and the rows that didn't. */
  showPeaks: boolean;
}

export interface ImportScreenState {
  intent: ImportIntent;
  entries: ImportFileEntry[];
  /** The expanded file, if any. One file starts expanded. */
  openId: string | null;
  /** Whether the user opened the "need a look" bar. */
  lookExpanded: boolean;
  isImporting: boolean;
  /** "Snap to paths" matches by file id, made when the box was ticked (importSnap.ts). */
  snaps?: Record<string, FileSnap>;
}

export const emptyScreen = (intent: ImportIntent): ImportScreenState => ({
  intent,
  entries: [],
  openId: null,
  lookExpanded: false,
  isImporting: false
});

export const isSingle = (s: ImportScreenState): boolean => s.entries.length === 1;

/** The files "Snap all to paths" ticks: every one imported as a planned hike from its own lines. */
export const snapAllEntries = (s: ImportScreenState): ImportFileEntry[] =>
  s.entries.filter(e => willImport(e) && e.settings.choice === 'planned' && !!e.preview && !e.preview.error);

/**
 * The "Snap all to paths" row above the list: shown with two or more such files, ticked once
 * every one of them is, and busy while any ticked one is still waiting for or being matched.
 */
export interface SnapAllRow {
  show: boolean;
  checked: boolean;
  busy: boolean;
}

export function snapAllRow(s: ImportScreenState): SnapAllRow {
  const files = snapAllEntries(s);
  return {
    show: !isSingle(s) && files.length >= 2,
    checked: files.length > 0 && files.every(e => e.settings.snapToPaths),
    busy: files.some(e => {
      const status = snapStatus(e, s.snaps?.[e.id]);
      return status === 'needed' || status === 'matching';
    })
  };
}

/** A ticked "Snap to paths" file that isn't matched yet: the Import button waits for it. */
export const snapPending = (s: ImportScreenState): boolean =>
  s.entries.some(e => {
    if (!willImport(e)) return false;
    const status = snapStatus(e, s.snaps?.[e.id]);
    return status === 'needed' || status === 'matching';
  });

export const findEntry = (s: ImportScreenState, id: string): ImportFileEntry | undefined => s.entries.find(e => e.id === id);

// ---- per file -------------------------------------------------------------------------

/** "Import as", in display order. */
export function options(s: ImportScreenState, e: ImportFileEntry): ImportChoice[] {
  const p = e.preview;
  if (!p || p.error) return [];
  const out: ImportChoice[] = [];
  if (p.canBe.includes('planned')) out.push('planned');
  if (p.canBe.includes('recording')) out.push('recording');
  if (p.canBe.includes('both')) out.push('both');
  if (p.canBe.includes('waypoints')) out.push('waypoints');
  if (p.canBe.includes('fromwpts')) out.push('fromWaypoints');
  if (p.canBe.includes('peaks')) out.push('peaks');
  out.push('skip');
  return out;
}

/** Whether the file's type sends it to the start screen; always true with no start screen. */
export function fitsStartScreen(s: ImportScreenState, e: ImportFileEntry): boolean {
  const home = intentDestination(s.intent);
  if (!home) return true;
  return choiceDestinations(e.defaultChoice).includes(home);
}

// A file going somewhere other than the start screen is listed with the rest, tagged with
// where it goes; only a file with nothing it can become waits behind the bar.
export function group(_s: ImportScreenState, e: ImportFileEntry): ImportFileGroup {
  if (entryError(e) || peakCheckError(e)) return 'error';
  if (isLoading(e)) return 'ready';
  return e.defaultChoice === 'skip' ? 'look' : 'ready';
}

/** Whether this file creates something when the button is pressed. */
export function willImport(e: ImportFileEntry): boolean {
  const p = e.preview;
  if (!p || p.error) return false;
  if (e.settings.choice === 'skip') return false;
  if (e.settings.choice === 'waypoints') return waypointsToAdd(p, e.settings).length > 0;
  if (e.settings.choice === 'peaks') return !!e.peakCheck && !e.peakCheck.error && e.peakCheck.newCount > 0;
  return true;
}

/** The "→ Planned hikes" tag: where the file goes other than the start screen; empty when nowhere. */
export function crossDestinations(s: ImportScreenState, e: ImportFileEntry): ImportDestination[] {
  const home = intentDestination(s.intent);
  if (!home) return [];
  return choiceDestinations(e.settings.choice).filter(d => d !== home);
}

export function badge(s: ImportScreenState, e: ImportFileEntry, choice: ImportChoice): ImportOptionBadge | null {
  // No start screen, or the file's type sends it elsewhere: flag the recommended option.
  // An option that fits the start screen carries no badge.
  if (intentDestination(s.intent) && fitsStartScreen(s, e)) return null;
  return choice !== 'skip' && choice === recommendedChoice(e) ? 'recommended' : null;
}

/** Whether an option reads "Goes to Planned hikes, not Recordings". */
export function goesElsewhere(s: ImportScreenState, choice: ImportChoice): boolean {
  const home = intentDestination(s.intent);
  if (!home || choice === 'skip') return false;
  return !choiceDestinations(choice).includes(home);
}

export function meta(s: ImportScreenState, e: ImportFileEntry): ImportFileMeta {
  const p = e.preview;
  if (!p) return { kind: 'reading' };
  if (p.error) return { kind: 'unreadable', error: p.error };
  if (isPeaksFile(e)) {
    if (!e.peakCheck) return { kind: 'reading' };
    if (e.peakCheck.error) return { kind: 'peaksError', error: e.peakCheck.error };
  }
  const settings = e.settings;
  switch (settings.choice) {
    case 'skip': {
      const needsLook = group(s, e) === 'look';
      return { kind: 'skipped', reason: needsLook ? e.skipReason : null, intent: s.intent, needsLook };
    }
    case 'recording':
      return { kind: 'recording', count: recordingCount(p), distanceM: recordingDistanceM(p), durationMs: recordingDurationMs(p) };
    case 'both':
      return {
        kind: 'both',
        recordings: recordingCount(p),
        days: plannedDays(p, settings, bothPlannedLines(p)).length,
        highlights: waypointsToAdd(p, settings).length
      };
    case 'planned':
      return {
        kind: 'planned',
        days: plannedDays(p, settings).length,
        distanceM: plannedLines(p, settings).reduce((sum, l) => sum + l.distanceM, 0),
        highlights: waypointsToAdd(p, settings).length
      };
    case 'waypoints':
      return { kind: 'waypoints', count: waypointsToAdd(p, settings).length };
    case 'fromWaypoints':
      return { kind: 'fromWaypoints', count: keptWaypoints(p).length, keptAsHighlights: settings.keepWaypoints };
    case 'peaks':
      return { kind: 'peaks', check: e.peakCheck as PeakCsvCheck };
  }
}

export function sections(s: ImportScreenState, e: ImportFileEntry): ImportFileSections {
  const p = e.preview;
  const choice = e.settings.choice;
  const lineCount = !p ? 0 : choice === 'both' ? bothPlannedLines(p).length : plannedLines(p, e.settings).length;
  const named = p?.waypointCounts.kept ?? 0;
  return {
    showPickHint: group(s, e) === 'look',
    options: options(s, e),
    showDays: (choice === 'planned' || choice === 'both') && lineCount > 1,
    dayCount: lineCount,
    showLines: choice === 'planned' && !!p && p.lines.some(l => l.duplicateOf !== null),
    showSnap: choice === 'planned' && !!p,
    snapStatus: snapStatus(e, s.snaps?.[e.id]),
    showWaypoints: named > 0 && (choice === 'planned' || choice === 'both' || choice === 'waypoints' || choice === 'fromWaypoints'),
    namedWaypoints: named,
    repeatedPoints: p?.waypointCounts.repeatsLine ?? 0,
    waypointsAsHighlights: choice !== 'waypoints',
    showPeaks: choice === 'peaks' && !!e.peakCheck && !e.peakCheck.error
  };
}

// ---- the whole screen -------------------------------------------------------------------

/** How many `d` one importing file creates: a recording per recorded track, a peak per new match, else one. */
export function createsCount(e: ImportFileEntry, d: ImportDestination): number {
  if (!choiceDestinations(e.settings.choice).includes(d)) return 0;
  if (d === 'peaks') return e.peakCheck?.newCount ?? 0;
  return d === 'recordings' && e.preview ? recordingCount(e.preview) : 1;
}

export function counts(s: ImportScreenState): ImportCounts {
  const importing = s.entries.filter(willImport);
  const to = (d: ImportDestination): number => importing.reduce((n, e) => n + createsCount(e, d), 0);
  return {
    recordings: to('recordings'),
    plannedHikes: to('plannedHikes'),
    waypointGroups: to('waypoints'),
    peaks: to('peaks')
  };
}

export function cta(s: ImportScreenState): ImportCta {
  const c = counts(s);
  if (countsTotal(c) === 0) return { kind: 'nothing' };
  return { kind: 'import', counts: c };
}

function readyOrder(e: ImportFileEntry): number {
  if (isLoading(e)) return 3;
  const d = choiceDestination(e.defaultChoice);
  return d ? IMPORT_DESTINATIONS.indexOf(d) : 3;
}

/** Files the start screen accepts, recordings → planned hikes → waypoints; unread files last. */
export function readyEntries(s: ImportScreenState): ImportFileEntry[] {
  return s.entries
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => group(s, e) === 'ready')
    .sort((a, b) => readyOrder(a.e) - readyOrder(b.e) || a.i - b.i)
    .map(({ e }) => e);
}

export const lookEntries = (s: ImportScreenState): ImportFileEntry[] => s.entries.filter(e => group(s, e) === 'look');

export const errorEntries = (s: ImportScreenState): ImportFileEntry[] => s.entries.filter(e => group(s, e) === 'error');

/** Null when nothing needs folding away. Open when the user opened it, or nothing is ready. */
export function lookBar(s: ImportScreenState): ImportLookBar | null {
  const look = lookEntries(s);
  const errors = errorEntries(s);
  if (!look.length && !errors.length) return null;
  return {
    lookCount: look.length,
    errorCount: errors.length,
    addedCount: look.filter(willImport).length,
    open: s.lookExpanded || readyEntries(s).length === 0
  };
}

/** What a file is: its default's destinations, or what it naturally is if it didn't fit. */
function tileTypes(e: ImportFileEntry): ImportDestination[] {
  const p = e.preview;
  if (!p || p.error) return [];
  const d = choiceDestinations(e.defaultChoice);
  if (d.length) return d;
  if (p.canBe.includes('recording')) return ['recordings'];
  if (p.lines.length) return ['plannedHikes'];
  return ['waypoints'];
}

/** One tile per type present, with the count that will be imported. */
export function tiles(s: ImportScreenState): ImportTile[] {
  const present = new Set(s.entries.flatMap(tileTypes));
  const c = counts(s);
  return IMPORT_DESTINATIONS.filter(d => present.has(d)).map(d => ({ destination: d, count: countOf(c, d) }));
}

/** Files that will import somewhere other than the start screen; for analytics. */
export const crossDestinationCount = (s: ImportScreenState): number =>
  s.entries.filter(e => willImport(e) && crossDestinations(s, e).length > 0).length;
